<template>
  <div class="consulta-productos-mes-container">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Filtros Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-filter me-2"></i>Productos Vendidos por Mes</h5>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- Mes -->
              <div class="col-md-4">
                <label class="form-label">Mes</label>
                <select v-model="filtros.mes" class="form-select">
                  <option value="1">Enero</option>
                  <option value="2">Febrero</option>
                  <option value="3">Marzo</option>
                  <option value="4">Abril</option>
                  <option value="5">Mayo</option>
                  <option value="6">Junio</option>
                  <option value="7">Julio</option>
                  <option value="8">Agosto</option>
                  <option value="9">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
              </div>

              <!-- Año -->
              <div class="col-md-4">
                <label class="form-label">Año</label>
                <input type="number" v-model="filtros.anio" class="form-control" min="2020" :max="currentYear">
              </div>

              <!-- Botón Buscar -->
              <div class="col-12">
                <button class="btn btn-primary me-2" @click="buscarProductos">
                  <i class="fas fa-search me-2"></i>
                  Buscar
                </button>
                <button class="btn btn-secondary" @click="limpiarFiltros">
                  <i class="fas fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas Generales -->
    <div class="row mb-4" v-if="reportData">
      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Productos</h6>
                <h3 class="mb-0">{{ reportData.total_productos }}</h3>
              </div>
              <div class="stat-icon bg-primary">
                <i class="fas fa-box"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Unidades Vendidas</h6>
                <h3 class="mb-0">{{ reportData.total_unidades_vendidas }}</h3>
              </div>
              <div class="stat-icon bg-success">
                <i class="fas fa-shopping-cart"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Período</h6>
                <h3 class="mb-0">{{ nombreMes }} {{ reportData.year }}</h3>
              </div>
              <div class="stat-icon bg-info">
                <i class="fas fa-calendar-alt"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Productos -->
    <div class="row" v-if="reportData">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-list me-2"></i>Productos Vendidos - {{ nombreMes }} {{
                  reportData.year }}</h5>
              </div>
              <div class="col-auto">
                <div class="d-flex align-items-center">
                  <label class="me-2 mb-0">Mostrar:</label>
                  <select v-model="paginacion.porPagina" class="form-select form-select-sm" style="width: auto;"
                    @change="cambiarPorPagina">
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover table-striped mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 80px;">Imagen</th>
                    <th>Producto</th>
                    <th class="text-end">Precio</th>
                    <th class="text-center">Cantidad Vendida</th>
                    <th class="text-end">Total Ventas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="reportData.productos.length === 0">
                    <td colspan="6" class="text-center py-4">
                      <i class="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                      <p class="text-muted mb-0">No se encontraron productos vendidos en el período seleccionado</p>
                    </td>
                  </tr>
                  <tr v-for="producto in productosPaginados" :key="producto.productoId">
                    <td>
                      <img :src="getImageUrl(producto.portada)" :alt="producto.titulo" class="product-thumbnail"
                        @error="handleImageError" />
                    </td>
                    <td>
                      <div class="fw-semibold">{{ producto.titulo }}</div>
                      <small class="text-muted">{{ producto.slug }}</small>
                    </td>
                    <td class="text-end">{{ formatoMoneda(producto.precio) }}</td>
                    <td class="text-center">
                      <span class="badge bg-primary">{{ producto.cantidad_vendida }}</span>
                    </td>
                    <td class="text-end fw-semibold text-success">
                      {{ formatoMoneda(producto.precio * producto.cantidad_vendida) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light" v-if="reportData.productos.length > 0">
                  <tr class="fw-bold">
                    <td colspan="3" class="text-end">TOTAL:</td>
                    <td class="text-center">
                      <span class="badge bg-primary fs-6">{{ reportData.total_unidades_vendidas }}</span>
                    </td>
                    <td class="text-end text-success">
                      {{ formatoMoneda(totalVentas) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Paginación -->
          <div class="card-footer" v-if="reportData.productos.length > 0">
            <div class="row align-items-center">
              <div class="col">
                <p class="mb-0 text-muted">
                  Mostrando {{ rangoInicio }} a {{ rangoFin }} de {{ reportData.productos.length }} productos
                </p>
              </div>
              <div class="col-auto">
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(paginacion.paginaActual - 1)">
                        <i class="fas fa-chevron-left"></i>
                      </a>
                    </li>
                    <li class="page-item" v-for="pagina in paginasVisibles" :key="pagina"
                      :class="{ active: pagina === paginacion.paginaActual }">
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">
                        {{ pagina }}
                      </a>
                    </li>
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(paginacion.paginaActual + 1)">
                        <i class="fas fa-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Mensaje inicial cuando no hay datos -->
    <div class="row" v-if="!reportData && !isLoading">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fas fa-box-open fa-4x text-muted mb-3"></i>
            <h5 class="text-muted">Selecciona un mes y año, luego haz clic en "Buscar" para ver los productos vendidos
            </h5>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'ConsultaProductosMes',
  data() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    return {
      isLoading: false,
      currentYear,
      filtros: {
        mes: currentMonth.toString(),
        anio: currentYear
      },
      reportData: null,
      paginacion: {
        paginaActual: 1,
        porPagina: 25
      },
      meses: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ]
    };
  },
  computed: {
    nombreMes() {
      return this.meses[parseInt(this.filtros.mes) - 1] || '';
    },
    productosPaginados() {
      if (!this.reportData || !this.reportData.productos) return [];
      const inicio = (this.paginacion.paginaActual - 1) * this.paginacion.porPagina;
      const fin = inicio + parseInt(this.paginacion.porPagina);
      return this.reportData.productos.slice(inicio, fin);
    },
    totalPaginas() {
      if (!this.reportData || !this.reportData.productos) return 0;
      return Math.ceil(this.reportData.productos.length / this.paginacion.porPagina);
    },
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginacion.paginaActual;
      const paginas = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          paginas.push(i);
        }
      } else {
        if (actual <= 4) {
          for (let i = 1; i <= 5; i++) paginas.push(i);
          paginas.push('...');
          paginas.push(total);
        } else if (actual >= total - 3) {
          paginas.push(1);
          paginas.push('...');
          for (let i = total - 4; i <= total; i++) paginas.push(i);
        } else {
          paginas.push(1);
          paginas.push('...');
          for (let i = actual - 1; i <= actual + 1; i++) paginas.push(i);
          paginas.push('...');
          paginas.push(total);
        }
      }

      return paginas.filter(p => p !== '...' || paginas.indexOf(p) === paginas.lastIndexOf(p));
    },
    rangoInicio() {
      if (!this.reportData || !this.reportData.productos) return 0;
      return (this.paginacion.paginaActual - 1) * this.paginacion.porPagina + 1;
    },
    rangoFin() {
      if (!this.reportData || !this.reportData.productos) return 0;
      const fin = this.paginacion.paginaActual * this.paginacion.porPagina;
      return fin > this.reportData.productos.length ? this.reportData.productos.length : fin;
    },
    totalVentas() {
      if (!this.reportData || !this.reportData.productos) return 0;
      return this.reportData.productos.reduce((sum, producto) => {
        return sum + (producto.precio * producto.cantidad_vendida);
      }, 0);
    }
  },
  mounted() {
    // Cargar datos del mes actual al iniciar
    this.buscarProductos();
  },
  methods: {
    async buscarProductos() {
      if (!this.filtros.mes || !this.filtros.anio) {
        this.$toast.warning('Por favor seleccione un mes y año válidos');
        return;
      }

      this.isLoading = true;

      try {
        const response = await api.get('/report/productos-mes', {
          params: {
            year: this.filtros.anio,
            month: parseInt(this.filtros.mes)
          }
        });

        // Verificar si la respuesta fue exitosa
        if (!response.data) {
          throw new Error('Error en la respuesta del servidor');
        }

        this.reportData = response.data;
        this.paginacion.paginaActual = 1;

        const mensaje = this.reportData.total_productos === 0
          ? 'No se encontraron productos vendidos en el período seleccionado'
          : `Se encontraron ${this.reportData.total_productos} productos vendidos`;

        this.$toast.success(mensaje);
      } catch (error) {
        console.error('Error al buscar productos:', error);
        this.reportData = null;

        let errorMessage = 'Error al cargar los productos vendidos';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$toast.error(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    limpiarFiltros() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      this.filtros = {
        mes: currentMonth.toString(),
        anio: currentYear
      };

      this.buscarProductos();
    },

    cambiarPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacion.paginaActual = pagina;
      }
    },

    cambiarPorPagina() {
      this.paginacion.paginaActual = 1;
    },

    formatoMoneda(value) {
      if (!value && value !== 0) return '$0,00';
      const numero = Number(value).toFixed(2);
      return '$' + numero
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    getImageUrl(portada) {
      const fileName = portada.split('/').pop();

      // Usar la misma estructura que listProducts.vue
      const baseURL = api.defaults.baseURL;
      return `${baseURL}/producto/obtener_image_producto/${fileName}`;
    },


    handleImageError(event) {
      event.target.src = '/img/placeholder.png';
    },

    getStockBadge(stock) {
      if (stock === 0) return 'bg-danger';
      if (stock < 10) return 'bg-warning text-dark';
      if (stock < 50) return 'bg-info';
      return 'bg-success';
    }
  }
};
</script>

<style scoped>
.consulta-productos-mes-container {
  padding: 0;
}

.stat-card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.table th {
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.table tfoot tr {
  background-color: #f8f9fa;
  border-top: 3px solid #dee2e6;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.form-label {
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-select,
.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
}

.form-select:focus,
.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.product-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.pagination {
  gap: 0.25rem;
}

.page-link {
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .product-thumbnail {
    width: 40px;
    height: 40px;
  }
}
</style>
